import { ProjectStatus } from '@control-tower/platform-wms';

/** Severity of the message shown inside a work order card. */
export type WorkOrderMessageSeverity = 'warning' | 'blocked' | 'info';

export interface WorkOrderSelectability {
    /** Whether the work order can be converted to will-call / hot-shot. */
    selectable: boolean;
    /** Severity of the inline message, if any. */
    severity?: WorkOrderMessageSeverity;
    /**
     * Message shown inside the card. For `blocked` it explains why the order
     * cannot be selected; for `warning` it cautions the user before selecting.
     */
    message?: string;
}

/** Default action wording when the calling screen does not supply one. */
const DEFAULT_ACTION_LABEL = 'will-call / hot shot';

/**
 * Resolves whether a work order can be selected from the will-call / hot-shot
 * search and what (if any) message to surface. Results are shown for every
 * status (no longer filtered out of the query) so the user always sees the
 * order they searched for:
 *   - selectable, no message: normal pickable statuses
 *   - selectable + warning: order is mid-pack; confirm before changing it
 *   - blocked: terminal/invalid statuses, with the reason
 *
 * @param actionLabel the action being performed ("will-call" / "hot shot"),
 *   woven into the packing warning so it reads correctly per screen.
 */
export function getWorkOrderSelectability(
    status: ProjectStatus | string | null | undefined,
    actionLabel: string = DEFAULT_ACTION_LABEL
): WorkOrderSelectability {
    switch (status) {
        case ProjectStatus.Pending:
        case ProjectStatus.Picking:
        case ProjectStatus.Picked:
        case ProjectStatus.ShortPick:
            return { selectable: true };

        case ProjectStatus.Packing:
            return {
                selectable: true,
                severity: 'warning',
                message: `Order is currently being packed — confirm with the packing employee before marking it as ${actionLabel}.`,
            };
        case ProjectStatus.Packed:
            return {
                selectable: true,
                severity: 'warning',
                message: `Order is already packed — confirm with the packing employee before marking it as ${actionLabel}.`,
            };

        case ProjectStatus.Shipped:
        case ProjectStatus.Completed:
            return {
                selectable: false,
                severity: 'blocked',
                message: 'Order has already been shipped.',
            };
        case ProjectStatus.ZeroPick:
            return {
                selectable: false,
                severity: 'blocked',
                message: 'Order was zero picked.',
            };
        case ProjectStatus.CloseShort:
            return {
                selectable: false,
                severity: 'blocked',
                message: 'Order is complete and marked as short.',
            };
        case ProjectStatus.Cancelled:
            return {
                selectable: false,
                severity: 'blocked',
                message: 'Order is cancelled.',
            };
        case ProjectStatus.Deleted:
            return {
                selectable: false,
                severity: 'blocked',
                message: 'Order has been deleted.',
            };

        default:
            return {
                selectable: false,
                severity: 'blocked',
                message: 'Order is not available for selection.',
            };
    }
}
